import { useState } from 'react';
import { DocumentResponse } from '../models/api-response';
import { getLogger } from '../utils/logger';

const logger = getLogger('useDocumentDownload');

interface UseDocumentDownloadProps {
  onClose?: () => void;
}

interface DocumentDownloadState {
  isVisible: boolean;
  documentId: string | null;
  base64Content: string | null;
}

/**
 * Hook for managing document download state and functionality
 */
export const useDocumentDownload = (props?: UseDocumentDownloadProps) => {
  const [downloadState, setDownloadState] = useState<DocumentDownloadState>({
    isVisible: false,
    documentId: null,
    base64Content: null
  });

  /**
   * Show document download
   * @param documentResponse The document response containing document ID and content
   */
  const showDownload = (documentResponse: DocumentResponse) => {
    logger.info("showDownload called with document ID:", documentResponse.documentId);
    
    try {
      // Check if docBase64 exists and is valid
      if (!documentResponse.docBase64) {
        logger.error("docBase64 is missing or empty in documentResponse");
        setDownloadState({
          isVisible: true,
          documentId: documentResponse.documentId,
          base64Content: null
        });
        return;
      }

      // Log the first few characters of the base64 content
      logger.info(`Base64 content download: ${documentResponse.docBase64.substring(0, 30)}...`);

      setDownloadState({
        isVisible: true,
        documentId: documentResponse.documentId,
        base64Content: documentResponse.docBase64
      });

      logger.info(`Download state set for document ${documentResponse.documentId}`);
    } catch (error) {
      logger.error('Failed to prepare document for download', JSON.stringify(error));
      // Show an error state or fallback
      setDownloadState({
        isVisible: true,
        documentId: documentResponse.documentId,
        base64Content: null
      });
    }
  };

  /**
   * Hide the document download
   */
  const hideDownload = () => {
    logger.info("Hiding document download");
    
    setDownloadState(prev => ({
      ...prev,
      isVisible: false
    }));

    if (props?.onClose) {
      props.onClose();
    }
  };

  return {
    isDownloadVisible: downloadState.isVisible,
    documentId: downloadState.documentId,
    base64Content: downloadState.base64Content,
    showDownload,
    hideDownload
  };
};